import { db } from './db';
import store from '@/store';
import uuid from '@/plugins/uuid';

class DocumentUploadService {
    constructor() {
        // Make these public so they can be set from outside
        this.apiUrl = null;
        this.bearerToken = null;
    }

    /**
     * Initialize the service with API credentials
     */
    init() {
        this.apiUrl = store.getters.getApiUrl;
        this.bearerToken = store.getters.getBearer;

        // If bearer is not available from getBearer, try other getters
        if (!this.bearerToken) {
            this.bearerToken = store.state.bearer ||
                              store.state.auth?.bearer ||
                              store.state.user?.bearer ||
                              store.getters.getBearerToken ||
                              store.getters.getAuthToken;
        }

        console.log('DocumentUploadService initialized:', {
            apiUrl: this.apiUrl,
            hasToken: !!this.bearerToken,
            tokenLength: this.bearerToken ? this.bearerToken.length : 0
        });
    }

    /**
     * Check if we're online (respects force offline mode)
     */
    get isOnline() {
        return store.getters.online;
    }

    /**
     * Supported document MIME types (from backend docs)
     */
    static get DOCUMENT_MIME_TYPES() {
        return {
            'application/pdf': 'pdf',
            'image/png': 'png',
            'image/jpg': 'jpg',
            'image/jpeg': 'jpg',
            'application/msword': 'doc',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
            'application/vnd.ms-excel': 'xls',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
            'text/csv': 'csv',
            'text/plain': 'txt'
        };
    }

    /**
     * Max file size (10MB)
     */
    static get MAX_FILE_SIZE() {
        return 10485760; // 10MB
    }

    /**
     * Convert base64 to blob
     */
    base64ToBlob(base64String, mimeType = 'application/pdf') {
        // Remove data URL prefix if present
        const base64Data = base64String.replace(/^data:[^;]+;base64,/, '');

        // Decode base64 string
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return new Blob([bytes], { type: mimeType });
    }

    /**
     * Extract MIME type from base64 string
     */
    getMimeType(base64String) {
        const match = base64String.match(/^data:([^;]+);base64,/);
        return match ? match[1] : 'application/pdf';
    }

    /**
     * Get file extension from MIME type
     */
    getExtension(mimeType) {
        return DocumentUploadService.DOCUMENT_MIME_TYPES[mimeType] || 'pdf';
    }

    /**
     * Validate file size
     */
    validateFileSize(fileSize) {
        if (fileSize > DocumentUploadService.MAX_FILE_SIZE) {
            throw new Error(`File size exceeds maximum of ${DocumentUploadService.MAX_FILE_SIZE / 1048576}MB`);
        }
        return true;
    }

    /**
     * Validate MIME type
     */
    validateMimeType(mimeType) {
        if (!DocumentUploadService.DOCUMENT_MIME_TYPES[mimeType]) {
            throw new Error(`Unsupported file type: ${mimeType}`);
        }
        return true;
    }

    /**
     * Request presigned URL from backend
     * IMPORTANT: For documents, ALL metadata is sent in this step (not in confirm!)
     */
    async requestPresignedUrl(entityType, entityUuid, fileName, fileSize, fileType, documentMetadata = {}, documentUuid = null, existingDocumentUuid = null) {
        // Check if we're online first (respects force offline mode)
        if (!this.isOnline) {
            throw new Error('Cannot request presigned URL while offline or in force offline mode');
        }

        // Try to init if credentials are missing
        if (!this.apiUrl || !this.bearerToken) {
            this.init();
        }

        // Final check - if still no bearer token, throw error
        if (!this.bearerToken) {
            throw new Error('No authentication token available. Please ensure you are logged in.');
        }

        // Validate file size and type
        this.validateFileSize(fileSize);
        this.validateMimeType(fileType);

        console.log('Requesting presigned URL for document:', {
            apiUrl: this.apiUrl,
            entityType,
            entityUuid,
            fileName,
            fileSize,
            fileType,
            documentUuid,
            existingDocumentUuid,
            metadata: documentMetadata
        });

        try {
            // Remove trailing slash if present and check if apiUrl already ends with /api
            const baseUrl = this.apiUrl.replace(/\/$/, '');
            const url = baseUrl.endsWith('/api')
                ? `${baseUrl}/v2/documents/prepare`
                : `${baseUrl}/api/v2/documents/prepare`;

            // CRITICAL: All metadata is sent in PREPARE step for documents
            const requestBody = {
                entity_type: entityType,
                entity_uuid: entityUuid,
                file_name: fileName,
                file_size: fileSize,
                file_type: fileType
            };

            // Include document UUID if provided (for tracking)
            if (documentUuid) {
                requestBody.document_uuid = documentUuid;
            }

            // Include existing_document_uuid for update operations
            if (existingDocumentUuid) {
                requestBody.existing_document_uuid = existingDocumentUuid;
            }

            // Add all document metadata (all optional)
            if (documentMetadata.name) {
                requestBody.name = documentMetadata.name;
            }
            if (documentMetadata.document_number) {
                requestBody.document_number = documentMetadata.document_number;
            }
            if (documentMetadata.document_date) {
                requestBody.document_date = documentMetadata.document_date;
            }
            if (documentMetadata.document_revision_date) {
                requestBody.document_revision_date = documentMetadata.document_revision_date;
            }
            if (documentMetadata.document_type_id) {
                requestBody.document_type_id = documentMetadata.document_type_id;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.bearerToken}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Presigned URL request failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    body: errorText,
                    url: url
                });

                // Check for specific error codes
                if (response.status === 401) {
                    console.error('Authentication failed. Token may be invalid or expired.');
                    throw new Error('Authentication failed (401) - Please check login status');
                }

                if (response.status === 404) {
                    console.warn('Presigned URL endpoint not found. The backend may not be ready yet.');
                    throw new Error('Presigned URL endpoint not available (404)');
                }

                throw new Error(`Failed to get presigned URL: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            console.log('Received presigned URL response:', data);

            // Ensure we have the expected structure
            if (!data.upload_url) {
                throw new Error('No upload URL in response');
            }

            // Note: Backend returns "fields" (not "upload_fields" like images)
            return {
                upload_url: data.upload_url,
                fields: data.fields || {},  // "fields" for documents, not "upload_fields"
                session_uuid: data.session_uuid,
                s3_key: data.s3_key || (data.fields && data.fields.key),
                expires_at: data.expires_at
            };
        } catch (error) {
            console.error('Error requesting presigned URL:', error);
            throw error;
        }
    }

    /**
     * Upload document to S3 using presigned URL
     */
    async uploadToS3(uploadUrl, uploadFields, documentBlob, onProgress = null) {
        try {
            console.log('Uploading to S3:', {
                uploadUrl,
                fields: uploadFields,
                blobSize: documentBlob.size,
                blobType: documentBlob.type
            });

            const formData = new FormData();

            // Get the actual content type from the blob
            const contentType = documentBlob.type || 'application/pdf';

            // Check if backend provided acl and Content-Type in fields
            const hasAcl = uploadFields && 'acl' in uploadFields;
            const hasContentType = uploadFields && 'Content-Type' in uploadFields;

            // Add acl if not provided by backend (required by S3 policy)
            if (!hasAcl) {
                formData.append('acl', 'private');
            }

            // Add Content-Type if not provided by backend (required by S3 policy)
            if (!hasContentType) {
                formData.append('Content-Type', contentType);
            }

            // IMPORTANT: Add fields in the exact order they came from the backend
            // The order matters for S3 presigned POST
            if (uploadFields) {
                Object.entries(uploadFields).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        formData.append(key, value);
                    }
                });
            }

            // Add the file as the last field (MUST be last for S3)
            formData.append('file', documentBlob, 'document');

            // Use fetch instead of axios to avoid CORS issues
            const response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
                // Don't set Content-Type header - let browser set it with boundary
            });

            console.log('S3 upload response:', {
                status: response.status,
                statusText: response.statusText,
                ok: response.ok
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('S3 upload failed:', errorText);

                // Check for expired presigned URL
                if (response.status === 403 &&
                    (errorText.includes('Request has expired') ||
                     errorText.includes('expired') ||
                     errorText.includes('Token expired'))) {
                    const expiredError = new Error('EXPIRED_URL: Presigned URL has expired');
                    expiredError.isExpiredUrl = true;
                    throw expiredError;
                }

                // Check for specific S3 policy errors that shouldn't be retried
                if (response.status === 403 || errorText.includes('AccessDenied') ||
                    errorText.includes('Policy Condition failed') ||
                    errorText.includes('Invalid according to Policy')) {
                    const policyError = new Error(`S3 Policy Error: ${errorText.substring(0, 200)}`);
                    policyError.isPolicyError = true;
                    policyError.permanent = true;
                    throw policyError;
                }

                throw new Error(`S3 upload failed: ${response.status} - ${errorText}`);
            }

            // Report progress as complete
            if (onProgress) {
                onProgress(100);
            }

            return true;
        } catch (error) {
            // Check if it's a CORS error
            if (error.message && error.message.includes('CORS')) {
                console.error('CORS error detected. S3 bucket needs CORS configuration:', error);
                const corsError = new Error('CORS_ERROR: S3 bucket is not configured for cross-origin requests');
                corsError.isCorsError = true;
                throw corsError;
            }

            // Check if it's a network error that might be CORS-related
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                console.error('Failed to fetch - likely CORS error:', error);
                const corsError = new Error('CORS_ERROR: Unable to upload to S3 - CORS policy blocking request');
                corsError.isCorsError = true;
                throw corsError;
            }

            console.error('Error uploading to S3:', error);
            throw error;
        }
    }

    /**
     * Confirm upload with backend
     * IMPORTANT: For documents, confirm is VERY SIMPLE - just session_uuid
     * (metadata was already sent in prepare step and stored in session)
     */
    async confirmUpload(sessionUuid) {
        // Check if we're online first (respects force offline mode)
        if (!this.isOnline) {
            throw new Error('Cannot confirm upload while offline or in force offline mode');
        }

        if (!this.apiUrl || !this.bearerToken) {
            this.init();
        }

        try {
            // Remove trailing slash if present and check if apiUrl already ends with /api
            const baseUrl = this.apiUrl.replace(/\/$/, '');
            const url = baseUrl.endsWith('/api')
                ? `${baseUrl}/v2/documents/confirm`
                : `${baseUrl}/api/v2/documents/confirm`;

            // CRITICAL: Document confirm is MUCH SIMPLER than images
            // Metadata is already in the session from the prepare step
            const requestBody = {
                session_uuid: sessionUuid
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.bearerToken}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorText = await response.text();

                // Check if it's a duplicate UUID error
                if ((response.status === 500 || response.status === 409 || response.status === 422) &&
                    errorText.includes('Duplicate entry')) {
                    console.warn(`Document already confirmed on backend, treating as success`);
                    const duplicateError = new Error('Document already exists');
                    duplicateError.isDuplicate = true;
                    throw duplicateError;
                }

                throw new Error(`Failed to confirm upload: ${response.status} - ${errorText.substring(0, 200)}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error confirming upload:', error);
            throw error;
        }
    }

    /**
     * Main upload flow for a single document with retry logic
     */
    async uploadDocument(document, entityType, entityUuid, onProgress = null, retryCount = 0) {
        console.log('Starting uploadDocument:', {
            documentUuid: document.uuid,
            entityType,
            entityUuid,
            hasUrl: !!document.url,
            retryCount,
            isOnline: this.isOnline,
            forceOffline: store.getters.getForceOffline,
            currentStatus: document.upload_status
        });

        // Check if already confirmed - no need to re-upload
        if (document.upload_status === 'confirmed') {
            console.log(`Document ${document.uuid} already confirmed, skipping upload`);
            return true;
        }

        // Check if we're online (respects force offline mode)
        if (!this.isOnline) {
            console.log('Not online or force offline is enabled, queueing document for later upload');
            await this.queueForLaterUpload(document, entityType, entityUuid);
            return false;
        }

        const MAX_RETRIES = 3;
        const RETRY_DELAY_BASE = 1000; // Start with 1 second

        try {
            // Check if document has base64 data
            if (!document.url || !document.url.startsWith('data:')) {
                throw new Error('Document missing base64 data');
            }

            // Update status to uploading
            await db.documents.update(document.uuid, { upload_status: 'uploading' });

            // Get MIME type and create blob
            const mimeType = this.getMimeType(document.url);
            const documentBlob = this.base64ToBlob(document.url, mimeType);
            const extension = this.getExtension(mimeType);
            const fileName = `document-${document.uuid}.${extension}`;

            console.log('Prepared document data:', {
                documentUuid: document.uuid,
                mimeType,
                blobSize: documentBlob.size,
                extension,
                fileName
            });

            // Prepare metadata for the document (sent in PREPARE step)
            const documentMetadata = {
                name: document.name,
                document_number: document.document_number,
                document_date: document.document_date,
                document_revision_date: document.document_revision_date,
                document_type_id: document.document_type_id
            };

            // Request presigned URL (with metadata!)
            const presignedResponse = await this.requestPresignedUrl(
                entityType,
                entityUuid,
                fileName,
                documentBlob.size,
                mimeType,
                documentMetadata,
                document.uuid
            );

            // Try to upload to S3
            try {
                const uploadSuccess = await this.uploadToS3(
                    presignedResponse.upload_url,
                    presignedResponse.fields,  // Note: "fields" not "upload_fields"
                    documentBlob,
                    onProgress
                );

                if (!uploadSuccess) {
                    throw new Error('S3 upload failed');
                }
            } catch (uploadError) {
                // Check if it's an expired URL error
                if (uploadError.isExpiredUrl) {
                    console.log('Presigned URL expired, requesting new URL and retrying...');

                    // Request a new presigned URL
                    const newPresignedResponse = await this.requestPresignedUrl(
                        entityType,
                        entityUuid,
                        fileName,
                        documentBlob.size,
                        mimeType,
                        documentMetadata,
                        document.uuid
                    );

                    // Try upload with new URL
                    const uploadSuccess = await this.uploadToS3(
                        newPresignedResponse.upload_url,
                        newPresignedResponse.fields,
                        documentBlob,
                        onProgress
                    );

                    if (!uploadSuccess) {
                        throw new Error('S3 upload failed with new presigned URL');
                    }

                    // Update presignedResponse for confirmation
                    Object.assign(presignedResponse, newPresignedResponse);
                } else {
                    // Re-throw other errors
                    throw uploadError;
                }
            }

            // Update local database with S3 key
            await db.documents.update(document.uuid, {
                s3_key: presignedResponse.s3_key,
                upload_status: 'uploaded',
                session_uuid: presignedResponse.session_uuid
            });

            // Confirm with backend (simple - just session_uuid)
            try {
                await this.confirmUpload(presignedResponse.session_uuid);

                // Update final status (keep base64 for now - can be cleaned up later manually)
                await db.documents.update(document.uuid, {
                    upload_status: 'confirmed',
                    sync_status: 'synced'
                });
            } catch (confirmError) {
                // If it's a duplicate UUID error, treat as success
                if (confirmError.isDuplicate || (confirmError.message && confirmError.message.includes('Document already exists'))) {
                    console.log(`Document ${document.uuid} already exists on backend, marking as confirmed`);
                    await db.documents.update(document.uuid, {
                        upload_status: 'confirmed',
                        sync_status: 'synced'
                    });
                    return true;
                }
                // Set status to failed before re-throwing
                await db.documents.update(document.uuid, {
                    upload_status: 'failed',
                    upload_error: `Confirm failed: ${confirmError.message}`
                });
                throw confirmError;
            }

            return true;
        } catch (error) {
            console.error('Error in uploadDocument:', error);

            // Check if it's a CORS error
            if (error.isCorsError || error.message.includes('CORS_ERROR')) {
                // Mark as permanently failed - CORS issues won't resolve with retries
                await db.documents.update(document.uuid, {
                    upload_status: 'cors_error',
                    upload_error: 'CORS configuration error - S3 bucket needs to be configured for cross-origin requests'
                });

                console.error('CORS error detected - not queuing for retry as this requires backend configuration');
                throw error;
            }

            // Check if it's an S3 Policy error
            if (error.isPolicyError || error.message.includes('Policy Error') ||
                error.message.includes('Policy Condition failed')) {
                // Mark as permanently failed
                await db.documents.update(document.uuid, {
                    upload_status: 'policy_error',
                    upload_error: error.message.substring(0, 200)
                });

                console.error('S3 Policy error detected - not retrying:', error.message);
                throw error;
            }

            // Check if we should retry (for transient errors)
            if (retryCount < MAX_RETRIES && !error.permanent) {
                const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount); // Exponential backoff
                console.log(`Retrying upload after ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);

                // Update status to show retry
                await db.documents.update(document.uuid, {
                    upload_status: 'retrying',
                    upload_error: `Retry ${retryCount + 1}/${MAX_RETRIES}: ${error.message}`
                });

                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, delay));

                // Retry the upload
                return this.uploadDocument(document, entityType, entityUuid, onProgress, retryCount + 1);
            }

            // Update status to failed
            await db.documents.update(document.uuid, {
                upload_status: 'failed',
                upload_error: error.message
            });

            // If offline, queue for later
            if (!this.isOnline) {
                await this.queueForLaterUpload(document, entityType, entityUuid);
            }

            throw error;
        }
    }

    /**
     * Queue document for later upload when offline
     */
    async queueForLaterUpload(document, entityType, entityUuid) {
        const jobUuid = uuid();
        await db.jobs.put({
            uuid: jobUuid,
            type: 'document-s3-upload',
            module: 'documents',
            url: null,
            attempt: 0,
            payload: {
                uuid: jobUuid,
                document_uuid: document.uuid,
                documentable_uuid: entityUuid,
                entity_type: entityType
            },
            created_at: new Date().toISOString()
        });
    }

    /**
     * Process all pending document uploads with concurrency limit
     * @param {number} concurrencyLimit - Maximum number of parallel uploads (default: 3)
     */
    async processPendingUploads(concurrencyLimit = 3) {
        if (!this.isOnline) {
            console.log('Cannot process uploads while offline');
            return;
        }

        try {
            // Get all documents pending upload
            const pendingDocuments = await db.documents
                .where('upload_status')
                .anyOf(['pending', 'failed'])
                .toArray();

            console.log(`Processing ${pendingDocuments.length} pending document uploads with concurrency limit of ${concurrencyLimit}`);

            // Process documents in batches with concurrency limit
            const results = await this.processBatchWithConcurrency(
                pendingDocuments,
                concurrencyLimit,
                async (document) => {
                    try {
                        // Find the entity type from the documentable_uuid
                        const entityInfo = await this.findEntityInfo(document.documentable_uuid);
                        if (entityInfo) {
                            await this.uploadDocument(
                                document,
                                entityInfo.type,
                                document.documentable_uuid
                            );
                            return { success: true, uuid: document.uuid };
                        }
                        return { success: false, uuid: document.uuid, error: 'Entity not found' };
                    } catch (error) {
                        console.error(`Failed to upload document ${document.uuid}:`, error);
                        return { success: false, uuid: document.uuid, error: error.message };
                    }
                }
            );

            const successful = results.filter(r => r.success).length;
            const failed = results.filter(r => !r.success).length;
            console.log(`Pending uploads processed: ${successful} successful, ${failed} failed`);

            return results;
        } catch (error) {
            console.error('Error processing pending uploads:', error);
            throw error;
        }
    }

    /**
     * Process array items in batches with concurrency limit
     * @param {Array} items - Items to process
     * @param {number} concurrencyLimit - Maximum concurrent operations
     * @param {Function} processFn - Async function to process each item
     */
    async processBatchWithConcurrency(items, concurrencyLimit, processFn) {
        const results = [];
        const executing = [];

        for (const item of items) {
            // Create promise for this item
            const promise = processFn(item).then(result => {
                // Remove from executing array when done
                executing.splice(executing.indexOf(promise), 1);
                return result;
            });

            results.push(promise);
            executing.push(promise);

            // If we've reached concurrency limit, wait for one to finish
            if (executing.length >= concurrencyLimit) {
                await Promise.race(executing);
            }
        }

        // Wait for all remaining promises to complete
        return Promise.all(results);
    }

    /**
     * Find entity type and info from UUID
     */
    async findEntityInfo(uuid) {
        // Check inspections
        const inspection = await db.inspections.where('uuid').equals(uuid).first();
        if (inspection) return { type: 'inspection', entity: inspection };

        // Check location_item_components
        const component = await db.location_item_components.where('uuid').equals(uuid).first();
        if (component) return { type: 'location_item_component', entity: component };

        return null;
    }

    /**
     * Cancel an upload
     */
    async cancelUpload(documentUuid) {
        await db.documents.update(documentUuid, {
            upload_status: 'cancelled'
        });
    }

    /**
     * Retry a failed upload
     */
    async retryUpload(documentUuid) {
        const document = await db.documents.get(documentUuid);
        if (!document) {
            throw new Error('Document not found');
        }

        const entityInfo = await this.findEntityInfo(document.documentable_uuid);
        if (!entityInfo) {
            throw new Error('Entity not found for document');
        }

        return this.uploadDocument(document, entityInfo.type, document.documentable_uuid);
    }

    /**
     * Update an existing document with new document data
     * This replaces the S3 file while keeping the same document UUID
     */
    async updateDocument(existingDocumentUuid, newDocumentData, entityType, entityUuid, onProgress = null, retryCount = 0) {
        console.log('Starting updateDocument:', {
            existingDocumentUuid,
            entityType,
            entityUuid,
            hasUrl: !!newDocumentData.url,
            retryCount,
            isOnline: this.isOnline
        });

        // Check if we're online (respects force offline mode)
        if (!this.isOnline) {
            console.log('Not online or force offline is enabled, queueing document update for later');
            await this.queueForLaterUpdate(existingDocumentUuid, newDocumentData, entityType, entityUuid);
            return false;
        }

        const MAX_RETRIES = 3;
        const RETRY_DELAY_BASE = 1000;

        try {
            // Check if new document has base64 data
            if (!newDocumentData.url || !newDocumentData.url.startsWith('data:')) {
                throw new Error('New document missing base64 data');
            }

            // Update status in database
            await db.documents.update(existingDocumentUuid, {
                upload_status: 'uploading',
                url: newDocumentData.url
            });

            // Get MIME type and create blob
            const mimeType = this.getMimeType(newDocumentData.url);
            const documentBlob = this.base64ToBlob(newDocumentData.url, mimeType);
            const extension = this.getExtension(mimeType);
            const fileName = `document-${uuid()}.${extension}`;

            console.log('Prepared updated document data:', {
                existingDocumentUuid,
                mimeType,
                blobSize: documentBlob.size,
                extension,
                fileName
            });

            // Prepare metadata
            const documentMetadata = {
                name: newDocumentData.name,
                document_number: newDocumentData.document_number,
                document_date: newDocumentData.document_date,
                document_revision_date: newDocumentData.document_revision_date,
                document_type_id: newDocumentData.document_type_id
            };

            // Request presigned URL with existing_document_uuid for update
            const presignedResponse = await this.requestPresignedUrl(
                entityType,
                entityUuid,
                fileName,
                documentBlob.size,
                mimeType,
                documentMetadata,
                uuid(), // New document UUID for the uploaded file
                existingDocumentUuid  // Pass the existing document UUID to replace
            );

            // Upload to S3
            try {
                const uploadSuccess = await this.uploadToS3(
                    presignedResponse.upload_url,
                    presignedResponse.fields,
                    documentBlob,
                    onProgress
                );

                if (!uploadSuccess) {
                    throw new Error('S3 upload failed');
                }
            } catch (uploadError) {
                // Handle expired URL
                if (uploadError.isExpiredUrl) {
                    console.log('Presigned URL expired, requesting new URL and retrying...');

                    const newPresignedResponse = await this.requestPresignedUrl(
                        entityType,
                        entityUuid,
                        fileName,
                        documentBlob.size,
                        mimeType,
                        documentMetadata,
                        uuid(),
                        existingDocumentUuid
                    );

                    const uploadSuccess = await this.uploadToS3(
                        newPresignedResponse.upload_url,
                        newPresignedResponse.fields,
                        documentBlob,
                        onProgress
                    );

                    if (!uploadSuccess) {
                        throw new Error('S3 upload failed with new presigned URL');
                    }

                    Object.assign(presignedResponse, newPresignedResponse);
                } else {
                    throw uploadError;
                }
            }

            // Update local database with new S3 key
            await db.documents.update(existingDocumentUuid, {
                s3_key: presignedResponse.s3_key,
                upload_status: 'uploaded',
                session_uuid: presignedResponse.session_uuid,
                updated_at: new Date().toISOString()
            });

            // Confirm with backend
            try {
                await this.confirmUpload(presignedResponse.session_uuid);

                // Update final status and clear base64 to save IndexedDB space
                await db.documents.update(existingDocumentUuid, {
                    upload_status: 'confirmed',
                    sync_status: 'synced',
                    url: null  // Clear base64 data after successful S3 upload
                });
            } catch (confirmError) {
                // Handle duplicate errors
                if (confirmError.isDuplicate || (confirmError.message && confirmError.message.includes('Document already exists'))) {
                    console.log(`Document ${existingDocumentUuid} update already confirmed on backend`);
                    await db.documents.update(existingDocumentUuid, {
                        upload_status: 'confirmed',
                        sync_status: 'synced',
                        url: null  // Clear base64 data after successful S3 upload
                    });
                    return true;
                }
                // Set status to failed before re-throwing
                await db.documents.update(existingDocumentUuid, {
                    upload_status: 'failed',
                    upload_error: `Confirm failed: ${confirmError.message}`
                });
                throw confirmError;
            }

            console.log(`Successfully updated document ${existingDocumentUuid}`);
            return true;

        } catch (error) {
            console.error('Error in updateDocument:', error);

            // Handle CORS and policy errors (no retry)
            if (error.isCorsError || error.isPolicyError || error.permanent) {
                await db.documents.update(existingDocumentUuid, {
                    upload_status: 'failed',
                    upload_error: error.message
                });
                throw error;
            }

            // Retry logic for transient errors
            if (retryCount < MAX_RETRIES) {
                const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount);
                console.log(`Retrying document update after ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);

                await db.documents.update(existingDocumentUuid, {
                    upload_status: 'retrying',
                    upload_error: `Retry ${retryCount + 1}/${MAX_RETRIES}: ${error.message}`
                });

                await new Promise(resolve => setTimeout(resolve, delay));
                return this.updateDocument(existingDocumentUuid, newDocumentData, entityType, entityUuid, onProgress, retryCount + 1);
            }

            // Max retries exceeded
            await db.documents.update(existingDocumentUuid, {
                upload_status: 'failed',
                upload_error: error.message
            });

            // Queue for later if offline
            if (!this.isOnline) {
                await this.queueForLaterUpdate(existingDocumentUuid, newDocumentData, entityType, entityUuid);
            }

            throw error;
        }
    }

    /**
     * Queue document update for later processing when offline
     */
    async queueForLaterUpdate(existingDocumentUuid, newDocumentData, entityType, entityUuid) {
        const jobUuid = uuid();
        await db.jobs.put({
            uuid: jobUuid,
            type: 'document-s3-update',
            module: 'documents',
            url: null,
            attempt: 0,
            payload: {
                uuid: jobUuid,
                existing_document_uuid: existingDocumentUuid,
                new_document_data: newDocumentData,
                documentable_uuid: entityUuid,
                entity_type: entityType
            },
            created_at: new Date().toISOString()
        });
    }
}

// Export singleton instance
export default new DocumentUploadService();
